import React from "react";
// Common components
import { Button, CheckBox, DynamicDropdown, Modal } from "src/Components";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
// Local Components
import AvgBubblePercentGraph from "./AvgBubblePercentGraph";
import NormalizedBubbleGraph from "./NormalizedBubbleGraph";
import NormalizedCalculatedBubbleGraph from "./NormalizedCalculatedBubbleGraph";
import NavDifferenceGraph from "./NavDifferenceGraph";
import ChartSearchParamUI from "./ChartSearchParamUI";
// Requests
import {
  getCashAssetDropdown,
  getGoldArbitrageTable,
  getPurchaseTable,
} from "src/Requests/goldArbitrage";
// Types
import {
  IInstrumentBubbleTableReport,
  InstrumentBubbleSearchParams,
  PurchaseSearchParams,
  IPurchaseTableReport,
} from "src/Interfaces/goldArbitrage";
import {
  IFilterTableCols,
  IExtraTypeUIProps,
} from "src/Tables/TableTemplate/tableTemplate";
import { ExtraType } from "src/Interfaces/TableInterfaces";
import InstrumentDomain from "./InstrumentDomain";
import PairConfig from "./PairConfig";
import Settings from "./Settings";

interface IVisibleHeaders {
  all: boolean;
  mediator: boolean;
}

enum ModalMode {
  Closed,
  Settings,
  InstrumentDomain,
  PairConfig,
}

const CashCalculations = () => {
  const [modal, setModal] = React.useState<ModalMode>(ModalMode.Closed);
  const [searchParam, setSearchParam] =
    React.useState<InstrumentBubbleSearchParams>(
      {} as InstrumentBubbleSearchParams,
    );
  const [visibleHeaders, setVisibleHeaders] = React.useState<IVisibleHeaders>(
    {} as IVisibleHeaders,
  );

  const fundChangeHandler = (name: string, value: number | string) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const changeHandler = (name: string, checked: boolean) => {
    setVisibleHeaders({
      ...visibleHeaders,
      [name]: checked,
    });
  };

  const getFilterCols = (): IFilterTableCols => {
    const result: IFilterTableCols = {
      show: true,
      tableName: "optionDomainPairPositionTable",
      allowedPositions: [],
    };
    if (visibleHeaders.all) result.allowedPositions.push("DefaultHidden");
    if (visibleHeaders.mediator) result.allowedPositions.push("Mediator");
    return result;
  };

  const closeModal = () => setModal(ModalMode.Closed);

  const getModalUI = () => {
    switch (modal) {
      case ModalMode.InstrumentDomain:
        return <InstrumentDomain />;
      case ModalMode.PairConfig:
        return <PairConfig />;
      case ModalMode.Settings:
        return <Settings />;
      default:
        return <></>;
    }
  };

  const getExtraTypeUI = ({
    headerName,
    extraType,
    row,
    cellType,
    translateHeaderName,
  }: IExtraTypeUIProps<IInstrumentBubbleTableReport>) => {
    if (extraType === ExtraType.WithChart) {
      switch (headerName) {
        case "AvgBubblePercent":
        case "CalculatedAvgBubblePercent":
          return (
            <AvgBubblePercentGraph {...{ row, headerName, translateHeaderName }} />
          );
        case "NormalizedBubble":
          return (
            <NormalizedBubbleGraph {...{ row, headerName, translateHeaderName }} />
          );
        case "NormalizedCalculatedBubble":
          return (
            <NormalizedCalculatedBubbleGraph
              {...{ row, headerName, translateHeaderName }}
            />
          );
        case "NavDifferencePercent":
          return (
            <NavDifferenceGraph {...{ row, headerName, translateHeaderName }} />
          );
        case "NavCalculated2":
          return (
            <NavDifferenceGraph
              {...{ row, headerName, translateHeaderName }}
              comparison
              refit
            />
          );
        case "NavCalculated2Delay":
          return (
            <NavDifferenceGraph
              {...{ row, headerName, translateHeaderName }}
              comparison
              delay
            />
          );
        default:
          return <></>;
      }
    }
    return <></>;
  };
  const getExtraPurchaseTypeUI = ({
    headerName,
    extraType,
    row,
    cellType,
    translateHeaderName,
  }: IExtraTypeUIProps<IPurchaseTableReport>) => {
    if (extraType === ExtraType.WithChart) {
      return (
        <ChartSearchParamUI {...{ row, headerName, translateHeaderName }} />
      );
    }
    return <></>;
  };

  return (
    <section className="w-full flex flex-col gap-4 ">
      {modal !== ModalMode.Closed ? (
        <Modal
          UI={getModalUI()}
          onClose={closeModal}
          modalBoxClassName="!min-w-min !min-h-min"
        />
      ) : (
        <></>
      )}
      <div className="flex flex-row justify-between items-start gap-4 w-full">
        <div className="w-56">
          <DynamicDropdown
            dropdown={{
              name: "instrumentId",
              placeholder: "صندوق",
              extraLabel: true,
            }}
            getData={getCashAssetDropdown}
            onChange={fundChangeHandler}
            value={searchParam.instrumentId}
          />
        </div>

        <div className="flex flex-row justify-start items-center gap-2">
          <div className="w-32">
            <Button
              onClick={() => setModal(ModalMode.PairConfig)}
              text="مرز اجرا"
              variant="outlined"
            />
          </div>
          <div className="w-32">
            <Button
              onClick={() => setModal(ModalMode.Settings)}
              text="تنظیمات"
              variant="outlined"
            />
          </div>
          <div className="w-32">
            <Button
              onClick={() => setModal(ModalMode.InstrumentDomain)}
              text="دامنه صندوق"
              variant="outlined"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-start items-center gap-5">
        <div className="bg-sky-200 rounded py-1 px-2">
          <CheckBox
            checked={visibleHeaders.all}
            label="جزئیات"
            name="all"
            onChange={changeHandler}
          />
        </div>
        <div className="bg-red-200 rounded py-1 px-2">
          <CheckBox
            checked={visibleHeaders.mediator}
            label="محاسبات"
            name="mediator"
            onChange={changeHandler}
          />
        </div>
      </div>

      <div className="w-full max-w-full overflow-x-auto">
        <TableTemplate<
          InstrumentBubbleSearchParams,
          IInstrumentBubbleTableReport
        >
          TableUI={({ reports, onAction, sortingType, setSortingType }) => (
            <CommonTableUI
              reports={reports}
              onAction={onAction}
              sortingType={sortingType}
              setSortingType={setSortingType}
              filterCols={getFilterCols()}
              tableBoxClassName="max-h-[350px]"
              getExtraTypeUI={getExtraTypeUI}
            />
          )}
          getDataTable={getGoldArbitrageTable}
          searchParameters={searchParam}
          refreshTableTime={15000}
          withSort
        />
      </div>
      <div className="w-full max-w-full overflow-x-auto">
        <TableTemplate<PurchaseSearchParams, IPurchaseTableReport>
          TableUI={({ reports, onAction }) => (
            <CommonTableUI
              reports={reports}
              onAction={onAction}
              tableBoxClassName="max-h-[350px]"
              showCellHoverTooltip
              getExtraTypeUI={getExtraPurchaseTypeUI}
            />
          )}
          getDataTable={getPurchaseTable}
          searchParameters={searchParam}
          refreshTableTime={15000}
        />
      </div>
    </section>
  );
};

export default CashCalculations;
